import { Subcontractors } from './../models/dtos/Classes/Subcontractors';
import { Injectable } from '@angular/core';
import {
  CostReviewDatabaseRecord,
  EstimateData,
  EstimateDatabaseRecord,
  EstimateSaveRequest,
  MiscLevelwiseData,
  SQWebProjectContainer
} from '../models/dtos';
import { CommonDataService } from './common-data.service';
import { IndexDBService } from './common/indexDB.service';
import { UserProfileService } from './common/user-profile.service';
import { EstimateCommonFunctionsService } from './estimate/estimate-common-functions.service';
import { ReviewDetailTabsService } from 'review-detail-tabs';
import { version } from '../../environments/environment';
import { CommonFunctionsService } from './common-functions.service';

@Injectable({
  providedIn: 'root'
})
export class EstimateSaveRequestBuilderService {
  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly indexDBService: IndexDBService,
    private readonly userProfileService: UserProfileService,
    private readonly commonCalcService: EstimateCommonFunctionsService,
    private readonly reviewDetailTabsService: ReviewDetailTabsService,
    private readonly commonFunctionService: CommonFunctionsService
  ) { }

  async createSaveEstimateRequest(): Promise<EstimateSaveRequest> {
    // Every save/export flow builds its request here, so pull in-progress review tab edits in first.
    this.reviewDetailTabsService.requestReviewFormDataSync();
    this.commonFunctionService.getCostReviewUpdatedRecord();
    const estimateData = structuredClone(this.commonDataService.getEstimateData());
    const costReviewData = structuredClone(this.commonDataService.getCostReviewData());
    const typicalOfHelperDictionary = structuredClone(this.commonDataService.getTypicalOfHelpers());
    Object.keys(typicalOfHelperDictionary).forEach(key => {
      typicalOfHelperDictionary[key].typicalOfSystems = [];
    });

    const [materialLevelwiseData, laborLevelwiseData, miscLevelwiseData, drawingLevelwiseData] = await Promise.all([
      this.indexDBService.getAllMaterialData(),
      this.indexDBService.getAllLaborData(),
      this.indexDBService.getAllMiscData(),
      this.indexDBService.getAllDrawingData()
    ]);


    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    estimateMetaData.estimateSettings.version = version;
    this.commonDataService.patchEstimateMetaData(estimateMetaData);

    await this.saveSubContractorCollectionInIndexDB(estimateMetaData.estimateTab.estimateTabSubContractors, miscLevelwiseData);

    const metaData = {
      estimateMetaData: structuredClone(this.commonDataService.getEstimateMetaData()),
      laborMetaData: structuredClone(this.commonDataService.getLaborMetaData()),
      miscMetaData: structuredClone(this.commonDataService.getMiscMetaData()),
      otherMetaData: structuredClone(this.commonDataService.getOtherMetaData())
    };

    const sqWebProject = structuredClone(this.commonDataService.getSQWebProject());

    const projectContainer: SQWebProjectContainer = {
      sqWebProject,
      materialLevelwiseData,
      laborLevelwiseData,
      miscLevelwiseData,
      drawingLevelwiseData,
      metaData,
      // Persist the "Typical Of" helper dictionary on the project object so that reopening a
      // web-saved estimate in the web app can reuse it directly instead of rebuilding it by iteration.
      // When a web-saved estimate is opened in the desktop app, the reverse mapping logic ignores this
      // helper (it is not mapped to any project object property), so desktop behaviour stays unchanged.
      typicalOfHelperDictionary: typicalOfHelperDictionary
    };

    return this.buildEstimateSaveRequest(estimateData, projectContainer, costReviewData);
  }

  // Mutates miscData in place so the save request picks up the updated values.
  async saveSubContractorCollectionInIndexDB(subcontractorCollection: Subcontractors[], miscData: MiscLevelwiseData): Promise<void> {
    if (!subcontractorCollection?.length) {
      return;
    }

    const estimateSubcontractorByKey = new Map<string, Subcontractors>();
    for (const estimateSubcontractor of subcontractorCollection) {
      estimateSubcontractorByKey.set(this.commonCalcService.getSubContractorKey(estimateSubcontractor), estimateSubcontractor);
    }

    for (const miscItem of Object.values(miscData.miscsCollection)) {
      for (const subcontractor of miscItem.subcontractorCollection ?? []) {
        const estimateSubcontractor = estimateSubcontractorByKey.get(this.commonCalcService.getSubContractorKey(subcontractor));
        if (estimateSubcontractor) {
          subcontractor.sellPrice = estimateSubcontractor.sellPrice;
          subcontractor.gmPercent = estimateSubcontractor.gmPercent;
          subcontractor.isLastModifiedSubContSellPrice = estimateSubcontractor.isLastModifiedSubContSellPrice;
        }
      }
    }

    await this.indexDBService.storeMiscData(miscData);
  }

  buildEstimateSaveRequest(
    estimateData: EstimateData,
    projectContainer: SQWebProjectContainer,
    costReviewData: CostReviewDatabaseRecord
  ): EstimateSaveRequest {
    const estimateDbRecord = this.buildEstimateDbRecord(estimateData, projectContainer, costReviewData);
    costReviewData.modifiedBy = this.userProfileService.userInfo.gid;

    return {
      estimateId: estimateData.estimateID,
      isCollaborativeEstimate: this.commonDataService.isCollaborationEstimate(),
      userId: this.userProfileService.userInfo.gid,
      userEmailId: '',
      business: estimateData.business,
      estimateData: estimateDbRecord,
      costReviewData: costReviewData,
      collabEstimateAdditionalInfo: ''
    };
  }

  private buildEstimateDbRecord(
    estimateData: EstimateData,
    projectContainer: SQWebProjectContainer,
    costReviewData: CostReviewDatabaseRecord
  ): EstimateDatabaseRecord {
    projectContainer.metaData.estimateMetaData.estimateSettings.culture = this.commonDataService.getUserLanguagePreference();
    return {
      estimateId: estimateData.estimateID,
      baseEstimateId: estimateData.baseEstimateId,
      estimateName: estimateData.estimateName,
      description: estimateData.description,
      estimateStatusId: estimateData.estimateStatusId,
      estimateTypeId: estimateData.estimateTypeId,
      author: estimateData.author,
      zoneId: estimateData.zoneId,
      areaId: estimateData.areaId,
      costCenterId: estimateData.costCenterId,
      segmentId: estimateData.profitCenterId,
      createdDate: estimateData.createdDate ? new Date(estimateData.createdDate) : null,
      customer: estimateData.customer,
      userTypeId: estimateData.userTypeId,
      isArchived: estimateData.isArchived,
      lastUploadDate: estimateData.lastUploadDate,
      modifiedBy: estimateData.modifiedBy,
      modifiedDate: estimateData.modifiedDate ? new Date(estimateData.modifiedDate) : null,
      startDate: estimateData.startDate ? new Date(estimateData.startDate) : null,
      endDate: estimateData.endDate ? new Date(estimateData.endDate) : null,
      projectObject: projectContainer,
      owner: estimateData.owner,
      salesOfficeNumber: estimateData.salesOfficeNumber,
      salesOfficeId: estimateData.salesOfficeId,
      version: projectContainer.metaData.estimateMetaData.estimateSettings.version?.toString(),
      createdTimestamp: 0,
      cultureId: this.commonFunctionService.getCultureId(this.commonDataService.getUserLanguagePreference()),
      sapSubmissionDate: estimateData.sapSubmissionDate ? new Date(estimateData.sapSubmissionDate) : null,
      culture: '',
      subTotalsCost: estimateData.subCost,
      marginPercent: estimateData.marginPercent,
      isMarkUpSelected: estimateData.isMarkUpSelected,
      taxTypeId: estimateData.taxTypeID,
      sellPrice: estimateData.sellPrice,
      isPPMEstimate: estimateData.isPPMEstimate,
      proposalId: estimateData.proposalID,
      lastProposalGeneratedDate: estimateData.lastProposalGeneratedDate ? new Date(estimateData.lastProposalGeneratedDate) : null,
      proposalLink: estimateData.proposalLink,
      subSegmentId: estimateData.subSegmentID,
      proposalTaskId: estimateData.proposalTaskID ? Number(estimateData.proposalTaskID) : null,
      isSummaryEstimate: estimateData.isSummaryEstimate,
      sellPriceProposalTaskId: estimateData.sellPriceProposalTaskID ? Number(estimateData.sellPriceProposalTaskID) : null,
      opportunitySellPrice: this.commonCalcService.calculateOpportunitySellPrice(estimateData),
      isNewEstimate: false,
      businessUnitId: projectContainer.metaData.estimateMetaData.estimateSettings.businessUnitId,
      isConversionRequired: true,
      customerId: '',
      siteId: '',
      processId: '',
      accountId: '',
      shipToAccountId: '',
      billToAccountId: '',
      endUserAccountId: '',
      shipToSAPNumber: '',
      endUserSAPNumber: '',
      billToSAPNumber: '',
      minMargin: 0,
      reasonForException: costReviewData?.reviewObject?.costReviewMarginExceptions?.reasonForException ?? '',
      isCostReviewOptional: false,
      isECS: estimateData.isECS ?? false,
    };
  }
}
